import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledH2,
    CheckboxIcon,
    StyledLabel,
} from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { uuid } from "uuidv4";

import { store } from "@components/VerticalLayout/store";
import {
    store as modeOfInformation,
    setNumberActive,
    setNumberArchived,
    setNumberActiveMultiShop,
    setNumberArchiveMultiShop,
    setIsLoading,
    setKey,
} from "./store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../api/hooks";
import { ArchiveAllModeOfInformation, groupByShopId } from "./helper";

type ConfirmationDeleteMessageType = {
    setIsModalOpened: Function;
    isModalOpened: boolean;
    setDataIsUpdated: Function;
    selectedRows: any;
    isOneShop: boolean;
};

export function ConfirmationActiveModeOfInformation({
    setIsModalOpened,
    isModalOpened,
    setDataIsUpdated,
    selectedRows,
    isOneShop,
}: ConfirmationDeleteMessageType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const {
        numberActive,
        numberArchived,
        numberActiveMultiShop,
        numberArchiveMultiShop,
        shopsModeInformationData,
    } = useSnapshot(modeOfInformation);
    const uuidUser = localStorage.getItem("uuidUser");

    async function activeModeInformation(isValidation: boolean) {
        setIsLoading(true);
        toast.dismiss();
        let localData: LocalData = [];
        selectedRows.forEach((element: any) => {
            let associated = isValidation
                ? ArchiveAllModeOfInformation(element, shopsModeInformationData)
                : [element];
            if (element.Id !== undefined) {
                associated.forEach((row: any) => {
                    let dataModeInformation = {
                        userId: userID,
                        operatorId: operatorID,
                        uuidUser: uuidUser,
                        shopId: row.shopId,
                        franchiseId: franchiseID,
                        isShop: !isOneShop,
                        data: {
                            [row.Id]: {
                                designation: row[t("Mode of information")],
                                idMI: row?.idMI,
                                supportOfSale: row[t("Sales support")],
                                salesSupportData: [row[t("Sales support")]],
                                displayName: row[t("Display name")],
                                modeOfSale: row[t("Sales method")],
                                tag: row.tag,
                                isArchived: false,
                                state: row.shopId === "0" ? true : row.state,
                                shopId: row.shopId,
                                stateShop: true,
                                advancedDisplayName: row.advancedDisplayName,
                            },
                        },
                    };
                    localData.push(dataModeInformation);
                });
            }
        });

        if (localData.length > 0) {
            localData = groupByShopId(localData);

            try {
                const response: any = await fetchGlobal(
                    `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_information`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(localData),
                    }
                );
                if (!response.error) {
                    toast.success(
                        `${t(
                            "Information mode has been successfully unarchived"
                        )}`,
                        {
                            position: toast.POSITION.TOP_CENTER,
                            theme: "colored",
                            icon: (
                                <CheckboxIcon
                                    height={25}
                                    width={25}
                                    fill="white"
                                />
                            ),
                        }
                    );

                    setKey(uuid());
                }
            } catch (e) {
                toast.error(`${t("There's an error")!}`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            } finally {
                setIsLoading(false);
            }
        }
    }

    function CountArchiveAndActiveNumber() {
        isOneShop
            ? setNumberActive(
                  selectedRows.length !== 0
                      ? numberActive + selectedRows.length
                      : numberActive + 1
              )
            : setNumberActiveMultiShop(
                  selectedRows.length !== 0
                      ? numberActiveMultiShop + selectedRows.length
                      : numberActiveMultiShop + 1
              );
        isOneShop
            ? setNumberArchived(
                  selectedRows.length !== 0
                      ? numberArchived - selectedRows.length
                      : numberArchived - 1
              )
            : setNumberArchiveMultiShop(
                  selectedRows.length !== 0
                      ? numberArchiveMultiShop - selectedRows.length
                      : numberArchiveMultiShop - 1
              );
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setDataIsUpdated(true);
                    // setGlobalDataIsUpdated;
                    setIsModalOpened(!isModalOpened);
                }}
            >
                <StyledH2 className="text-uppercase">{t("Alert")}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        "Attention! the Mode d'information is archived at store level.\n\nDo you want to activate it in shops too?"
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                        activeModeInformation(false);
                        CountArchiveAndActiveNumber();
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        activeModeInformation(true);
                        setIsModalOpened(!isModalOpened);
                        CountArchiveAndActiveNumber();
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
